import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSupplierRetailer } from "./SupplierRetailerContext";
import { usePageAccess } from "./PageAccessContext";

const AuthContext = createContext();

export const useAuthentication = () => useContext(AuthContext);

// localStorage keys that describe *the machine*, not *the session*. Sign-out
// must not wipe these — clearing `staySignedIn` is what used to silently
// un-tick "Remember this device" every time someone signed out, so the next
// sign-in minted no refresh token and the reload bounced them back to /signin.
const DEVICE_PREF_KEYS = ["staySignedIn", "spearbi_nav_collapsed"];

// A session is restorable only if the user asked us to remember the device
// AND we hold both halves of the credential (token + who it belongs to).
const hasStoredSession = () =>
  localStorage.getItem("staySignedIn") === "true" &&
  !!localStorage.getItem("refreshToken") &&
  !!localStorage.getItem("userid");

const clearSessionKeys = () => {
  const keep = DEVICE_PREF_KEYS.reduce((acc, k) => {
    const v = localStorage.getItem(k);
    if (v !== null) acc[k] = v;
    return acc;
  }, {});
  localStorage.clear();
  Object.entries(keep).forEach(([k, v]) => localStorage.setItem(k, v));
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user_type, setUserType] = useState(null);
  // True only while we are exchanging a stored refresh token on a cold load.
  // RequireAuth holds the route instead of redirecting for as long as this is
  // set, which is what actually keeps a reload from dumping you on /signin.
  const [restoring, setRestoring] = useState(() => hasStoredSession());
  const navigate = useNavigate();
  const { fetchUserAccess, resetContext: resetSupplierRetailerContext } = useSupplierRetailer();
  const { fetchPageAccess, resetContext: resetPageAccessContext } = usePageAccess();

  const signIn = (user_type = null) => {
    // New Data popup dismiss is per session — a fresh sign-in must show it
    // again while items are pending (TASK-new-data-tab.md §6 decision 3).
    try {
      sessionStorage.removeItem("spearbi_newdata_dismissed");
    } catch (e) {
      // ignore — sessionStorage may be unavailable
    }
    setIsAuthenticated(true);
    if (user_type !== null) {
      setUserType(user_type);
      localStorage.setItem("user_type", user_type);
    }
  };

  // Cold-load session restore. Runs once, inside the provider, *before* the
  // router is allowed to decide anything — the old version lived in a hook on
  // AppRoutes and lost the race to RequireAuth's <Navigate> every single time.
  useEffect(() => {
    if (!restoring) return;
    let cancelled = false;

    const run = async () => {
      const apiUrl = process.env.REACT_APP_API_URL;
      const refreshToken = localStorage.getItem("refreshToken");

      // A cold laptop, a reconnecting VPN or a sleeping API dyno must not cost
      // the user their session, so a failed *call* is retried. Only a clean
      // "this token is no good" answer from the server ends the session.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const r = await axios.post(`${apiUrl}/login/refresh`, { refreshToken });
          const row = r.status === 201 ? r.data?.data?.[0] : null;

          if (!row) {
            // 2xx but not 201 — the server rejected the token itself
            // (expired / revoked / unknown). Retrying cannot help.
            clearSessionKeys();
            break;
          }

          localStorage.setItem("userid", row.userid);
          localStorage.setItem("fname", row.fname);
          localStorage.setItem("user_type", row.user_type || "default");
          if (row.username) localStorage.setItem("username", row.username);
          // Rotated / slid token, when the API sends one back.
          if (r.data.refreshToken) localStorage.setItem("refreshToken", r.data.refreshToken);
          // Fresh access token from the refresh exchange — store before the
          // (authenticated) access/page fetches below.
          if (r.data.accessToken) localStorage.setItem("accessToken", r.data.accessToken);

          await fetchUserAccess(row.userid);
          await fetchPageAccess(row.userid);
          if (cancelled) return;

          setUserType(row.user_type || "default");
          setIsAuthenticated(true);
          setRestoring(false);
          return;
        } catch (err) {
          // Transport-level failure. Keep the token and try again shortly.
          if (attempt < 2) {
            await sleep(500 * 2 ** attempt);
            if (cancelled) return;
          }
        }
      }

      if (cancelled) return;
      setRestoring(false);
    };

    run();
    return () => {
      cancelled = true;
    };
    // Once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    // Revoke the refresh token server-side so it can't be replayed if the
    // device is shared. Best-effort — never block sign-out on a network call.
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) {
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/login/sign-out`, { refreshToken });
      } catch (e) {
        // ignore — local clear is what matters
      }
    }
    setIsAuthenticated(false);
    setUserType(null);
    setRestoring(false);
    resetSupplierRetailerContext();
    resetPageAccessContext();
    try {
      sessionStorage.removeItem("spearbi_newdata_dismissed");
    } catch (e) {
      // ignore — sessionStorage may be unavailable
    }
    clearSessionKeys();
    navigate("/signin");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user_type, restoring, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
